import { uniqueId } from "lodash-es";
import type { Group, TrackType } from "./Base";
import { videoDecoder, splitClip } from "@/utils/webcodecs";
import { OffscreenSprite } from "@webav/av-cliper";
import { UnitFrame2μs } from "@/stores/clip/data/trackConfig";
import trackState from "../trackState";

export interface VideoMaterial {
  id: string;
  url: string;
  name: string;
  format: string;
  duration: number;
  width: number;
  height: number;
}

export class VideoGroup implements Group {
  id: string;
  type: TrackType = "video";
  materials: VideoMaterial[];
  activeSourceIndex = 0;
  name: string;
  format: string;
  frameCount: number;
  start: number;
  end: any;
  centerX: number;
  centerY: number;
  scale: number;
  height: number;
  width: number;
  offsetL: number;
  offsetR: number;
  constructor(materials: VideoMaterial[], options: any = {}) {
    // 设置ID
    this.id = uniqueId();
    // 设置视频信息
    this.materials = materials;
    // 获取文件名称
    this.name = options.name ?? "镜头";
    // 获取文件类型
    this.format = options.format ?? "";
    // 设置轨道信息
    if (!trackState.isClip) {
      this.frameCount = options.frameCount ?? 150;
    } else {
      this.frameCount = Math.max.apply(
        null,
        this.materials.map((source) => (source.duration / 1000) * 30)
      );
    }
    this.start = options.start ?? 0;
    this.end = this.start + this.frameCount;

    // 设置绘制信息
    this.centerX = options.centerX ?? 0;
    this.centerY = options.centerY ?? 0;
    this.scale = options.scale || 1;
    // this.height = source.height;
    // this.width = source.width;

    // 设置裁剪信息
    this.offsetL = 0;
    this.offsetR = 0;
  }

  get drawHeight() {
    return this.height;
  }
  get drawWidth() {
    return this.width;
  }

  get source() {
    return this.materials[this.activeSourceIndex];
  }
  getDrawX(width: number) {
    // console.log("getDrawX", width, this.drawWidth, this.centerX);

    return width / 2 - this.drawWidth / 2 + this.centerX;
  }
  getDrawY(height: number) {
    // console.log("getDrawY", height, this.drawHeight, this.centerY);
    return height / 2 - this.drawHeight / 2 + this.centerY;
  }
  setActiveSourceIndex(index: number) {
    this.activeSourceIndex = index;
  }
  /**
   * 渲染需要优化
   * TODO: 不需要没一次都去解码
   * TODO: 优化画布渲染
   */
  draw(
    ctx: CanvasRenderingContext2D,
    { width, height }: { width: number; height: number },
    frameIndex: number
  ) {
    const frame = Math.max(frameIndex - this.start + this.offsetL, 1); // 默认展示首帧
    const start = performance.now();
    return videoDecoder.getFrame(this.source.id, frame).then(async (vf) => {
      if (vf) {
        console.log(
          "渲染耗时",
          vf,
          frame,
          ctx,
          this.source.width,
          this.source.height,
          this.getDrawX(width),
          this.getDrawY(height),
          this.drawWidth,
          this.drawHeight
        );
        ctx.drawImage(
          vf,
          0,
          0,
          this.source.width,
          this.source.height,
          this.getDrawX(width),
          this.getDrawY(height),
          this.drawWidth,
          this.drawHeight
        );
        vf?.close();
      }
    });
  }
  resize({ width, height }: { width: number; height: number }) {
    // 视频、图片元素，在添加到画布中时，需要缩放为合适的尺寸，目标是能在画布中完整显示内容
    let scale = 1;
    console.log("resize-------", this.materials, this.activeSourceIndex);

    if (this.source.width > width) {
      scale = width / this.source.width;
    }
    if (this.source.height > height) {
      scale = Math.min(scale, height / this.source.height);
    }
    this.width = this.source.width * scale;
    this.height = this.source.height * scale;
    console.log("resize----------", this.width, this.height);
  }
  audio: HTMLAudioElement | null = null;
  play(currentFrame: number) {
    console.log("play", currentFrame, this.audio);
    if (!this.audio) {
      this.audio = new Audio(this.materials[this.activeSourceIndex].url);
    }
    if (this.audio?.paused) {
      this.audio.currentTime = (currentFrame - this.start + this.offsetL) / 30;
      this.audio.play();
    }
  }
  pause() {
    if (this.audio && !this.audio.paused) {
      this.audio.pause();
    }
  }
  // 生成合成对象
  async combine(
    playerSize: { width: number; height: number },
    outputRatio: number
  ) {
    const video = await videoDecoder.decode({ id: this.source.id });
    const clip = await splitClip(video, {
      offsetL: this.offsetL,
      offsetR: this.offsetR,
      frameCount: this.frameCount,
    });
    if (!clip) {
      throw new Error("clip is not ready");
    }
    const spr = new OffscreenSprite(clip);
    // TODO：需要支持裁剪
    spr.time = {
      offset: this.start * UnitFrame2μs,
      duration: (this.end - this.start) * UnitFrame2μs,
    };
    spr.rect.x = this.getDrawX(playerSize.width) * outputRatio;
    spr.rect.y = this.getDrawY(playerSize.height) * outputRatio;
    spr.rect.w = this.drawWidth * outputRatio;
    spr.rect.h = this.drawHeight * outputRatio;

    return spr;
  }
  split(cutFrame: number) {
    // this.end = cutFrame;
    // this.offsetR = this.frameCount + this.start - cutFrame; // 根据cutFrame对视频进行分割
    // // 根据cutFrame对视频进行分割
    // const copy = new VideoGroup(this.source, cutFrame);
    // copy.offsetL = cutFrame - this.start;
    // return copy;
  }
}
